/**
 * HubCreationStore — wizard draft state for the Event Hub creation flow.
 *
 * Mirrors the Events CreationStore pattern (event-emitter + localStorage draft)
 * but scoped to a single Hub draft. Screens patch the draft; the shell reads it
 * for the phase tracker, summary, live-lock, and publish. Persistence to the
 * backend goes through hubs-data's saveHub / publishHubOp (guarded ops).
 */

import { useEffect, useState } from "react";
import type { Hub, HubKind, HubBranding, HubSeo, HubVisibility, HubFilterRule } from "./hubs-data";
import { eventsForHub } from "./hubs-data";
import type { UIEvent } from "../../lib/events-adapter";
import type { LandingBlock } from "../landing/landing-builder-helpers";
import { t as i18nT } from "../../lib/i18n";
import { getActiveTenantSlug, onTenantSwitching } from "../../shell/access";

export interface HubDraft extends Partial<Hub> {
  kind: HubKind;
  name: string;
  slug: string;
  visibilityMode: "public" | "private"; // public = anyone with the link / indexed; private = sign-in required
  branding: HubBranding;
  seo: HubSeo;
  visibility: HubVisibility;
  filterRule: HubFilterRule;
  /** Events the user has explicitly added on top of the filter-rule auto-pull.
   *  Adding/removing these is allowed WHILE LIVE (no take-offline needed). */
  manualEventIds: string[];
  /** Events the filter rule would include but the user pinned OUT. */
  removedEventIds: string[];
  /** Hub page design (block-based, same engine as landing pages). */
  pageBlocks: LandingBlock[];
  pageTheme: string;
  pageFont?: string;
  _editingId?: string | null;
  _published?: boolean;
  _wasLive?: boolean;
}

const LS_KEY_BASE = "cd:studio:hub-creation-draft:v1";
const hubDraftLsKey = () => `${LS_KEY_BASE}:${getActiveTenantSlug() ?? "none"}`;
const DEFAULT_VIS: HubVisibility = { indexed: true, showPastEvents: true, showCapacityRemaining: false, requireSignIn: false };

/** Starter hub page: a hero, then the events grid. */
export function defaultHubBlocks(): LandingBlock[] {
  const uid = () => `b${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`;
  return [
    { uid: uid(), type: "hero", cfg: { headline: "", subhead: "", bgKind: "gradient", from: "#75ADFA", to: "#5142C7", padding: 44, align: "left", showCTA: false } },
    { uid: uid(), type: "events", cfg: { title: i18nT("hubs.creation.upcomingEvents", "Upcoming events"), cols: 2, layout: "grid", showFilters: true } },
  ];
}

function blankDraft(kind: HubKind = "workspace"): HubDraft {
  return {
    kind, name: "", slug: "", description: "",
    visibilityMode: "public",
    branding: { theme: kind === "group" ? "Dark" : "Light", primaryColor: "#5142C7" },
    seo: {},
    visibility: { ...DEFAULT_VIS },
    filterRule: { kind },
    manualEventIds: [],
    removedEventIds: [],
    pageBlocks: defaultHubBlocks(),
    pageTheme: "brand",
    status: "draft",
  };
}

/** Membership for a draft hub = the filter-rule AUTO-PULL (eventsForHub: byTag /
 *  slug-as-tag / location / legacy heuristics) − pinned-out removedEventIds
 *  + manual adds, manual order first. This is the semantics the draft model has
 *  documented all along ("events explicitly added on top of the filter-rule
 *  auto-pull") but no wizard surface ever computed: they all read
 *  manualEventIds alone, which loadHub seeds EMPTY — so every loaded hub showed
 *  zero events (verifier, 2026-08-14). Every membership read goes through here;
 *  do not filter on manualEventIds directly. */
export function draftEvents(d: HubDraft, all: UIEvent[]): UIEvent[] {
  const manual = new Set(d.manualEventIds);
  const removed = new Set(d.removedEventIds);
  const byId = new Map(all.map((e) => [e.id, e]));
  const manualOrdered = d.manualEventIds.map((id) => byId.get(id)).filter(Boolean) as UIEvent[];
  const ruled = eventsForHub(d as unknown as Hub, all).filter((e) => !manual.has(e.id) && !removed.has(e.id));
  return [...manualOrdered, ...ruled];
}

function rehydrate(): HubDraft | null {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(hubDraftLsKey()) : null;
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && typeof d === "object" && typeof d.kind === "string") return d as HubDraft;
    return null;
  } catch { return null; }
}

let _draft: HubDraft = rehydrate() || blankDraft();
const _subs = new Set<() => void>();
function _persist() { try { if (typeof localStorage !== "undefined") localStorage.setItem(hubDraftLsKey(), JSON.stringify(_draft)); } catch { /* noop */ } }
const _emit = () => { _persist(); _subs.forEach((f) => { try { f(); } catch { /* noop */ } }); };
// Tenant-switch handling (docket 2026-07-27, item 1): same class as the
// events creation draft — purely local, safe to key off the optimistic identity.
onTenantSwitching(() => {
  _draft = rehydrate() || blankDraft();
  _subs.forEach((f) => { try { f(); } catch { /* noop */ } });
});

export const HubCreationStore = {
  get: (): HubDraft => _draft,
  subscribe: (l: () => void) => { _subs.add(l); return () => _subs.delete(l); },

  /** Start a fresh hub of `kind`. */
  startNew: (kind: HubKind) => { _draft = blankDraft(kind); _emit(); },

  /** Load an existing hub for edit — prefills the draft from its saved fields. */
  loadHub: (h: Hub) => {
    _draft = {
      ...blankDraft(h.kind),
      ...h,
      visibilityMode: h.visibility?.requireSignIn ? "private" : "public",
      branding: { ...blankDraft(h.kind).branding, ...(h.branding || {}) },
      seo: { ...(h.seo || {}) },
      visibility: { ...DEFAULT_VIS, ...(h.visibility || {}) },
      filterRule: { kind: h.kind, ...(h.filterRule || {}) },
      manualEventIds: [],
      removedEventIds: [],
      _editingId: h.id,
      _published: h.status === "published",
      _wasLive: h.status === "published",
    };
    _emit();
  },

  patch: (p: Partial<HubDraft>) => { _draft = { ..._draft, ...p }; _emit(); },
  patchBranding: (p: Partial<HubBranding>) => { _draft = { ..._draft, branding: { ..._draft.branding, ...p } }; _emit(); },
  patchSeo: (p: Partial<HubSeo>) => { _draft = { ..._draft, seo: { ..._draft.seo, ...p } }; _emit(); },
  patchVisibility: (p: Partial<HubVisibility>) => { _draft = { ..._draft, visibility: { ..._draft.visibility, ...p } }; _emit(); },
  patchFilter: (p: Partial<HubFilterRule>) => { _draft = { ..._draft, filterRule: { ..._draft.filterRule, ...p } }; _emit(); },
  setPage: (p: { pageBlocks?: LandingBlock[]; pageTheme?: string; pageFont?: string }) => { _draft = { ..._draft, ...p }; _emit(); },

  /** Add / remove an event from the manual include set (allowed while live). */
  addEvent: (id: string) => {
    const man = new Set(_draft.manualEventIds); man.add(id);
    const rem = _draft.removedEventIds.filter((x) => x !== id);
    _draft = { ..._draft, manualEventIds: [...man], removedEventIds: rem }; _emit();
  },
  removeEvent: (id: string) => {
    const man = _draft.manualEventIds.filter((x) => x !== id);
    const rem = new Set(_draft.removedEventIds); rem.add(id);
    _draft = { ..._draft, manualEventIds: man, removedEventIds: [...rem] }; _emit();
  },

  /** Reorder the manual event set (drag-reorder / sort in the Live manager).
   *  Keeps only ids that are currently members; appends any member missing
   *  from `ids` at the end (safety against a stale caller). */
  setEventOrder: (ids: string[]) => {
    const members = new Set(_draft.manualEventIds);
    const ordered = ids.filter((id) => members.has(id));
    for (const id of _draft.manualEventIds) if (!ordered.includes(id)) ordered.push(id);
    _draft = { ..._draft, manualEventIds: ordered }; _emit();
  },

  setPublished: () => { _draft = { ..._draft, _published: true, _wasLive: true, status: "published" }; _emit(); },
  takeOffline: () => { _draft = { ..._draft, _published: false, status: "draft" }; _emit(); },

  reset: () => { _draft = blankDraft(); _emit(); },
};

export function useHubDraft(): HubDraft {
  const [, force] = useState(0);
  useEffect(() => {
    const unsub = HubCreationStore.subscribe(() => force((n) => n + 1));
    return () => { unsub(); };
  }, []);
  return HubCreationStore.get();
}

// ── Wizard routing + validation ──────────────────────────────────────────────
export const HUB_FLOW = ["hub.create.basics", "hub.create.page", "hub.create.publish", "hub.create.live"] as const;

export interface HubCheck { label: string; route: string; done: boolean }
/** Every hard requirement to publish a hub, with the step that owns it. */
export function requiredHubChecks(d: HubDraft, eventCount: number): HubCheck[] {
  const has = (v: unknown) => v != null && String(v).trim() !== "";
  return [
    { label: i18nT("hubs.creation.hubName", "Hub name"), route: "hub.create.basics", done: has(d.name) },
    { label: i18nT("hubs.creation.urlSlug", "URL slug"), route: "hub.create.basics", done: has(d.slug) },
    { label: i18nT("hubs.creation.hubType", "Hub type"), route: "hub.create.basics", done: has(d.kind) },
    { label: i18nT("hubs.creation.description", "Description"), route: "hub.create.basics", done: has(d.description) },
    { label: i18nT("hubs.creation.atLeastOneEvent", "At least one event"), route: "hub.create.page", done: eventCount > 0 },
  ];
}

/** Whether the hub is live (published this session or a stored published hub not
 *  taken offline). Page-design edits lock while live; event add/remove does not. */
export function hubIsLive(d: HubDraft): boolean {
  return !!d._published;
}

/** slugify a hub name into a URL slug. */
export function hubSlugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
}
